import { Transition } from "@headlessui/react"
import { Head, router, usePage } from "@inertiajs/react"
import { Copy, Eye, EyeOff, RefreshCw } from "lucide-react"
import { useState } from "react"

import HeadingSmall from "@/components/heading-small"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { t } from "@/lib/i18n"
import { settingsMcpTokenPath } from "@/routes"

interface McpTokenProps {
  mcp_token: string
  last_mcp_access_at: string | null
}

export default function McpToken() {
  const { mcp_token, last_mcp_access_at } = usePage<McpTokenProps>().props
  const [showToken, setShowToken] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    void navigator.clipboard.writeText(mcp_token)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleRegenerate = () => {
    if (
      confirm(
        t("settings.mcp_token.regenerate_confirm") ||
          "Are you sure? Your old token will stop working immediately.",
      )
    ) {
      router.put(settingsMcpTokenPath())
    }
  }

  const formatLastAccess = (timestamp: string | null) => {
    if (!timestamp) return t("settings.mcp_token.never_used")

    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return t("settings.mcp_token.just_now")
    if (diffMins < 60)
      return t("settings.mcp_token.minutes_ago", { count: diffMins })
    if (diffHours < 24)
      return t("settings.mcp_token.hours_ago", { count: diffHours })
    if (diffDays < 7)
      return t("settings.mcp_token.days_ago", { count: diffDays })

    return date.toLocaleDateString()
  }

  const maskedToken = showToken
    ? mcp_token
    : "•".repeat(Math.min(mcp_token.length, 32))

  return (
    <DashboardLayout>
      <Head title={t("settings.mcp_token")} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title={t("settings.mcp_token.title")}
            description={t("settings.mcp_token.subtitle")}
          />

          <div className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="mcp_token">
                {t("settings.mcp_token.your_token")}
              </Label>

              <div className="flex gap-2">
                <Input
                  id="mcp_token"
                  type="text"
                  value={maskedToken}
                  readOnly
                  className="font-mono"
                />

                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setShowToken(!showToken)}
                  title={
                    showToken
                      ? t("settings.mcp_token.hide")
                      : t("settings.mcp_token.show")
                  }
                >
                  {showToken ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                  title={t("settings.mcp_token.copy")}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>

              <Transition
                show={copied}
                enter="transition ease-in-out"
                enterFrom="opacity-0"
                leave="transition ease-in-out"
                leaveTo="opacity-0"
              >
                <p className="text-muted-foreground text-sm">
                  {t("settings.mcp_token.copied")}
                </p>
              </Transition>
            </div>

            {last_mcp_access_at && (
              <div className="text-muted-foreground text-sm">
                {t("settings.mcp_token.last_access")}:{" "}
                {formatLastAccess(last_mcp_access_at)}
              </div>
            )}

            <Alert>
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">
                    {t("settings.mcp_token.how_to_use")}
                  </p>
                  <p className="text-sm">
                    {t("settings.mcp_token.instructions")}
                  </p>
                  <code className="bg-muted block rounded p-2 text-xs">
                    {`${window.location.origin}/mcp/sse?token=${showToken ? mcp_token : "YOUR_TOKEN"}`}
                  </code>
                </div>
              </AlertDescription>
            </Alert>

            <div className="pt-4">
              <Button
                type="button"
                variant="destructive"
                onClick={handleRegenerate}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {t("settings.mcp_token.regenerate")}
              </Button>
            </div>
          </div>
        </div>
      </SettingsLayout>
    </DashboardLayout>
  )
}
